<template>
    <div class="flex flex-col h-full" :style="fromReatlas ? 'max-width: 700px' : 'max-width: 100%;'">
        <div class="w-full site-title-bar border-b-2 border-blue-800 shadow-lg py-3" :class="{ 'cursor-move': isModal }">
            <div class="container flex items-center justify-between w-full" :class="{ 'px-5': isModal || fromReatlas, 'mx-auto': ! fromReatlas }">
                <div class="flex items-center">
                    <div class="group flex flex-1 flex-col text-white leading-normal" :class="{ 'hover:text-blue-100': ! isModal && ! fromReatlas }">
                        <div v-if="! isModal && ! fromReatlas" @click="showSites" class="flex cursor-pointer">
                            <div>
                                <div class="text-xl font-semibold uppercase">{{ site.building.address_line_1 }}</div>
                                <div class="text-sm uppercase">{{ site.building.city }}, {{ site.building.state }}</div>
                            </div>
                            <div class="flex">
                                <inline-svg name="chevron-down" classes="fill-current text-white group-hover:text-blue-100 h-5 w-5 ml-3 cursor-pointer"></inline-svg>
                            </div>
                        </div>
                        <div v-else>
                            <div class="font-semibold uppercase" :class="isModal ? 'text-3xl' : 'text-xl'">{{ site.building.address_line_1 | truncate(12) }}</div>
                            <div class="uppercase" :class="isModal ? 'text-base' : 'text-sm'">{{ site.building.city }}, {{ site.building.state }}</div>
                        </div>
                    </div>
                    <div class="flex items-center" :class="fromReatlas ? 'ml-6' : 'ml-16'">
                        <div
                            class="badge px-7 py-4 w-10 h-10 flex justify-center items-center text-base font-extrabold rounded-lg shadow-md"
                            :class="site.utilization_color"
                            title="Utilization">
                            {{ site.utilization }}%
                        </div>
                        <div v-if="site.archived_at != null" class="ml-6 flex items-center badge bg-orange-500 border rounded">
                            <inline-svg name="archived" classes="w-4 h-4 fill-current text-white"></inline-svg>
                            <span class="ml-2 text-white font-variant-all-small-caps">Archived</span>
                        </div>
                    </div>
                </div>
                <div class="flex items-center space-x-4">
                    <a v-if="fromReatlas" :href="site.view_url" class="btn btn-small btn-blue" target="_blank">
                        Go to Site Page
                    </a>

                    <div v-if="! isModal">
                        <div class="group/dropdown dropdown px-4 py-2 bg-gray-50 rounded-md border border-gray-200">
                            <div class="flex items-center">
                                <span class="text-gray-600 group-hover/dropdown:text-gray-500 font-semibold">Manage Site</span>
                                <inline-svg name="chevron-down" classes="ml-1 fill-current text-gray-500 w-6 h-6"></inline-svg>
                            </div>
                            <div class="dropdown-menu">
                                <ul>
                                    <li v-if="canEdit() && ! fromReatlas" class="group/items space-x-2 hover:bg-gray-100">
                                        <inline-svg name="edit" classes="text-gray-300 group-hover/items:text-blue-500 fill-current w-5 h-5"></inline-svg>
                                        <button @click="gotoEditBuilding" type="button" class="text-base text-gray-500">Edit Building</button>
                                    </li>
                                    <li class="group/items space-x-2 hover:bg-gray-100">
                                        <inline-svg name="document-text" classes="text-gray-300 group-hover/items:text-indigo-500 fill-current w-5 h-5"></inline-svg>
                                        <button @click="openManageLeases('lease-info')" type="button" class="text-base text-gray-500">Leases</button>
                                    </li>
                                    <li class="group/items space-x-2 hover:bg-gray-100">
                                        <inline-svg name="money" classes="text-gray-300 group-hover/items:text-green-500 fill-current w-5 h-5"></inline-svg>
                                        <button @click="openManageLeases('expenses')" type="button" class="text-base text-gray-500">Expenses</button>
                                    </li>
                                    <li class="group/items space-x-2 hover:bg-gray-100">
                                        <inline-svg name="calendar" classes="text-gray-300 group-hover/items:text-teal-500 fill-current w-5 h-5"></inline-svg>
                                        <button @click="openManageLeases('key-dates')" type="button" class="text-base text-gray-500">Key Dates</button>
                                    </li>
                                    <li v-if="site.archived_at == null" class="group/items space-x-2 hover:bg-gray-100">
                                        <inline-svg name="archived" classes="text-gray-300 group-hover/items:text-orange-500 fill-current w-4 h-4"></inline-svg>
                                        <button @click="archiveSite" type="button" class="text-base text-gray-500">Archive Site</button>
                                    </li>
                                    <li v-if="site.archived_at != null" class="group/items space-x-2 hover:bg-gray-100">
                                        <inline-svg name="archived" classes="text-gray-300 group-hover/items:text-orange-500 fill-current w-4 h-4"></inline-svg>
                                        <button @click="unarchiveSite" type="button" class="text-base text-gray-500">Unarchive Site</button>
                                    </li>
                                    <li v-if="canDelete()" class="group/items space-x-2 hover:bg-gray-100">
                                        <inline-svg name="trash" classes="text-gray-300 group-hover/items:text-red-500 fill-current w-4 h-4"></inline-svg>
                                        <button @click="confirmDeleteSite" type="button" class="text-base text-gray-500">Delete Site</button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div v-if="isModal || fromReatlas" @click="$emit('reatlas-close-right-panel')">
                        <inline-svg name="close" classes="fill-current text-white hover:text-gray-300 h-6 w-6 ml-5 cursor-pointer"></inline-svg>
                    </div>
                </div>
            </div>
        </div>

        <div id="site-container" class="flex-1 overflow-y-auto" :class="{ 'px-5': isModal || fromReatlas }">
            <div class="py-5 mx-auto container">
                <a name="pictures"></a>
                <div class="flex flex-col">
                    <div class="flex mb-5">
                        <div class="flex-1 mr-4 overflow-hidden">
                            <media-gallery
                                :documentable-id="site.id"
                                documentable-type="App\Site"
                                :media="site.media"
                                :height="500"
                                :object-cover="! fromReatlas">
                            </media-gallery>
                        </div>
                        <div v-if="! fromReatlas" class="w-2/5 shadow-md">
                            <map-component
                                :latitude="site.building.latitude"
                                :longitude="site.building.longitude"
                                :zoom="6">
                            </map-component>
                        </div>
                        <div v-if="fromReatlas">
                            <h2 class="text-sm font-bold uppercase">
                                Quick Facts
                            </h2>

                            <table class="mt-3">
                                <tbody>
                                    <tr>
                                        <td class="px-3 font-semibold">Property Type</td>
                                        <td>{{ site.building.type.type }}</td>
                                    </tr>
                                    <tr>
                                        <td class="px-3 font-semibold">Region</td>
                                        <td>{{ site.building.region?.name || '-' }}</td>
                                    </tr>
                                    <tr>
                                        <td class="px-3 font-semibold">LEED</td>
                                        <td>{{ site.building.leed_certification }}</td>
                                    </tr>
                                    <tr>
                                        <td class="px-3 font-semibold">Lease/Own</td>
                                        <td>{{ site.lease_or_own }}</td>
                                    </tr>
                                    <tr>
                                        <td class="px-3 font-semibold">Due in</td>
                                        <td>
                                            <div v-if="site.lease_or_own == 'Owned'">Owned</div>
                                            <div v-else-if="site.leases.length && site.due_in_days > 0">Due in <span class="font-bold">{{ site.due_in_text }}</span></div>
                                            <div v-else-if="site.leases.length && site.due_in_days == 0">Expired</div>
                                            <div v-else>No Lease Information</div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="flex items-center justify-around bg-brand text-white mb-8 p-4 shadow-md">
                        <div class="flex flex-col items-center leading-normal text-shadow space-y-1">
                            <div data-precision="0" :data-originalvalue="site.lease_cost" class="is-currency font-semibold" :class="! fromReatlas ? 'text-xl lg:text-3xl' : 'text-xl'">{{ formatCurrency(site.lease_cost, 0) }}</div>
                            <div class="font-bold uppercase" :class="! fromReatlas ? 'text-lg' : 'text-sm'">Total Cost</div>
                        </div>
                        <div v-if="! fromReatlas" class="flex flex-col items-center leading-normal text-shadow space-y-1">
                            <div data-precision="0" :data-originalvalue="site.remaining_lease_cost" class="is-currency font-semibold" :class="! fromReatlas ? 'text-xl lg:text-3xl' : 'text-xl'">{{ formatCurrency(site.remaining_lease_cost, 0) }}</div>
                            <div class="font-bold uppercase" :class="! fromReatlas ? 'text-lg' : 'text-sm'">Remaining Cost</div>
                        </div>
                        <div class="flex flex-col items-center leading-normal text-shadow space-y-1">
                            <div data-precision="2" :data-originalvalue="site.base_psf" class="is-currency font-semibold" :class="! fromReatlas ? 'text-xl lg:text-3xl' : 'text-xl'">{{ formatCurrency(site.base_psf, 2) }}</div>
                            <div class="font-bold uppercase" :class="! fromReatlas ? 'text-lg' : 'text-sm'">Base PSF</div>
                        </div>
                        <div class="flex flex-col items-center leading-normal text-shadow space-y-1">
                            <div data-precision="2" :data-originalvalue="site.current_psf" class="is-currency font-semibold" :class="! fromReatlas ? 'text-xl lg:text-3xl' : 'text-xl'">{{ formatCurrency(site.current_psf, 2) }}</div>
                            <div class="font-bold uppercase" :class="! fromReatlas ? 'text-lg' : 'text-sm'">Current PSF</div>
                        </div>
                        <div class="flex flex-col items-center leading-normal text-shadow space-y-1">
                            <div data-precision="0" :data-originalvalue="site.current_annual_cost" class="is-currency font-semibold" :class="! fromReatlas ? 'text-xl lg:text-3xl' : 'text-xl'">{{ formatCurrency(site.current_annual_cost, 0) }}</div>
                            <div class="font-bold uppercase" :class="! fromReatlas ? 'text-lg' : 'text-sm'">Current Annual</div>
                        </div>
                    </div>

                    <div>
                        <div class="border-b border-gray-200">
                            <div v-if="isModal || fromReatlas" class="mt-0 mb-3 flex items-center">
                                <span class="text-gray-700 font-medium">Viewing Section</span>
                                <select v-model="selectedTab" class="ml-2 mt-0 form-select form-select-blue max-w-fit">
                                    <option v-for="(tab, slug) in enabledTabs" :key="slug"  :value="slug">{{ tab.label }}</option>
                                </select>
                            </div>
                            <nav v-if="! fromReatlas" class="-mb-px flex space-x-2" aria-label="Tabs">
                                <button
                                    v-for="(tab, slug) in enabledTabs" :key="slug"
                                    @click="selectedTab = slug"
                                    class="py-2 px-3 border border-gray-200 flex items-center whitespace-nowrap rounded-t-lg text-sm" :class="slug != selectedTab ? tab.classes : tab.selectedClasses">
                                        {{ tab.label }}
                                    <span
                                        v-if="tab.showCount"
                                        class="py-0.5 px-2.5 ml-3 hidden rounded-full text-xs font-medium md:inline-block"
                                        :class="{ 'bg-gray-200 text-gray-900': slug != selectedTab, 'bg-blue-800 text-white': slug == selectedTab }">
                                            {{ tab.count }}
                                    </span>
                                </button>
                            </nav>
                        </div>
                    </div>

                    <div class="mb-6" :class="{ 'pl-5 border-l': ! fromReatlas }">
                        <div class="mt-6">
                            <building-fields v-if="selectedTab == 'building'" :building-data="site.building" :from-reatlas="fromReatlas" :from-site-id="site.id"></building-fields>

                            <leases v-if="selectedTab == 'leases'" :data="site" :from-reatlas="fromReatlas"></leases>

                            <expenses v-if="selectedTab == 'expenses'" :data="site.leases" :site-id="site.id" :from-reatlas="fromReatlas"></expenses>

                            <site-utilization
                                v-if="selectedTab == 'utilization'"
                                :show-industrial-chart="! fromReatlas"
                                :from-reatlas="fromReatlas">
                            </site-utilization>

                            <key-dates v-if="selectedTab == 'key_dates'" :sites="{site}" :from-reatlas="fromReatlas"></key-dates>

                            <documents
                                v-if="selectedTab == 'documents'"
                                :document-data="site.documents"
                                :documentable-id="site.id"
                                documentable-type="App\Site"
                                :from-reatlas="fromReatlas"
                            ></documents>

                            <contacts
                                v-if="selectedTab == 'contacts'"
                                :contact-data="site.contacts"
                                attach-to="site"
                                :attach-to-id="site.id">
                            </contacts>

                            <building-danger-zone
                                v-if="selectedTab == 'dangerZone'"
                                :building-data="site.building"
                                :view-only-mode="true">
                            </building-danger-zone>

                            <comments
                                v-if="selectedTab == 'comments'"
                                :data="site.comments"
                                :document-data="site.documents"
                                :commentable-id="site.id"
                                commentable-type="App\Site"
                            ></comments>

                            <site-imports
                                v-if="selectedTab == 'site_imports'"
                                :site-id="site.id"
                                :initial-customers="site.site_imports || []"
                                @imported="handleSiteImports"
                            ></site-imports>

                            <div v-if="$gate.isAdmin()">
                                <change-log v-if="selectedTab == 'changelog'" :data="site.audits"></change-log>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <notifications classes="notification" position="bottom right"></notifications>

        <modal @opened="$eventHub.$emit('convert-currency')" name="site-cards" width="1300px" height="auto" click-to-close scrollable adaptive>
            <div class="p-8">
                <site-cards
                    view="table"
                    :per-page="7"
                    :site-data="sites.all"
                    :show-create-new="false"
                    :show-client-info="false"
                    :show-filters="false"
                    small-header
                >
                </site-cards>
            </div>
        </modal>

        <modal name="manage-leases" width="60%" height="auto" click-to-close draggable=".modal-header">
            <manage-leases :selectedSection="leaseSection"></manage-leases>
        </modal>
    </div>
</template>

<script>
    import { mapState } from 'vuex';

    export default {
        name: 'ReoptSite',

        components: {
        },
        
        props: {
            'data': {
                type: Object,
                required: true
            },

            'openTab': {
                type: String,
                default: 'building'
            },

            'isModal': {
                type: Boolean,
                default: false
            },

            'fromReatlas': {
                type: Boolean,
                default: false
            }
        },

        data() {
            return {
                selectedTab: this.openTab,
                leaseSection: 'lease-info',
                tabClasses: 'bg-gray-50 text-gray-500 font-medium hover:border-gray-200 hover:text-gray-700',
                selectedTabClasses: 'bg-gradient-to-br from-blue-500 to-blue-700 font-bold text-white'
            }
        },

        created() {
            this.sites.selectedSite = this.data;
        },

        mounted() {
            this.buildings.selectedBuilding = this.site.building;

            this.leases.all = this.leases.filtered = this.site.leases;
            this.leases.selectedLease = this.site.leases.length ? this.site.leases[0] : null;

            this.contacts.all = this.site.contacts;
            this.contacts.filtered = this.site.contacts;

            this.documents.all = this.site.documents;
            this.documents.filtered = this.site.documents;

            localStorage.setItem(`reopt_baseCurrency`, this.site.currency);

            let selectedCurrency = localStorage.getItem(`reopt_selectedCurrency`) || 'USD';

            if (this.site.currency != selectedCurrency) {
                this.$eventHub.$emit('convert-currency', {
                    from: this.site.currency,
                    to: selectedCurrency
                });
            }

            this.$store.dispatch('loadSites');

            this.$eventHub.$on('site-saved', site => {
                this.site = site;
                this.sites.selectedSite = site;
            })
        },

        watch: {
            data() {
                this.sites.selectedSite = this.data;
                this.leases.all = this.data.leases;
                this.leases.filtered = this.data.leases;
                this.leases.selectedLease = this.data.leases[0] || {};
                this.documents.all = this.data.documents;
                this.documents.filtered = this.data.documents;
            }
        },

        computed: {
            enabledTabs() {
                // v-for used to be paired with v-if="tab.enabled"; Vue 2 runs
                // v-for first so the list was already filtered. Same result, and
                // it does not break when the directive priority reverses in Vue 3.
                const source = this.tabs;
                return Array.isArray(source)
                    ? source.filter(entry => entry.enabled)
                    : Object.fromEntries(Object.entries(source || {}).filter(([, entry]) => entry.enabled));
            },

            ...mapState(['sites']),
            ...mapState(['buildings']),
            ...mapState(['documents']),
            ...mapState(['leases']),
            ...mapState(['contacts']),

            site: {
                get() {
                    return this.sites.selectedSite;
                },
                set(value) {
                    // Update the site in the Vuex store
                    this.$store.commit('UPDATE_SITE', value);
                }
            },

            tabs() {
                return {
                    building: {
                        label: "Building",
                        enabled: true,
                        showCount: false,
                        classes: this.tabClasses,
                        selectedClasses: this.selectedTabClasses
                    },
                    leases: {
                        label: "Leases",
                        enabled: true,
                        showCount: true,
                        count: this.site.leases?.length || 0,
                        classes: this.tabClasses,
                        selectedClasses: this.selectedTabClasses
                    },
                    expenses: {
                        label: "Expenses",
                        enabled: true,
                        showCount: true,
                        count: this.site.expense_count,
                        classes: this.tabClasses,
                        selectedClasses: this.selectedTabClasses
                    },
                    utilization: {
                        label: "Utilization",
                        enabled: true,
                        showCount: false,
                        classes: this.tabClasses,
                        selectedClasses: this.selectedTabClasses
                    },
                    key_dates: {
                        label: "Key Dates",
                        enabled: true,
                        showCount: true,
                        count: this.site.key_dates?.length || 0,
                        classes: this.tabClasses,
                        selectedClasses: this.selectedTabClasses
                    },
                    documents: {
                        label: "Documents",
                        enabled: true,
                        showCount: true,
                        count: this.documents.all.length || 0,
                        classes: this.tabClasses,
                        selectedClasses: this.selectedTabClasses
                    },
                    contacts: {
                        label: "Contacts",
                        enabled: true,
                        showCount: true,
                        count: this.contacts.all?.length || 0,
                        classes: this.tabClasses,
                        selectedClasses: this.selectedTabClasses
                    },
                    site_imports: {
                        label: "Site Imports",
                        enabled: true,
                        showCount: false,
                        count: this.site.site_imports?.length || 0,
                        classes: this.tabClasses,
                        selectedClasses: this.selectedTabClasses
                    },
                    comments: {
                        label: "Comments",
                        enabled: true,
                        showCount: true,
                        count: this.site.comments?.length || 0,
                        classes: this.tabClasses,
                        selectedClasses: this.selectedTabClasses
                    },
                    dangerZone: {
                        label: "Danger Zone",
                        enabled: true,
                        showCount: false,
                        classes: this.site.building.is_in_danger_zone ? 'bg-gradient-to-br from-red-500 to-red-700 font-bold text-white' : this.tabClasses,
                        selectedClasses: this.site.building.is_in_danger_zone ? 'bg-gradient-to-br from-red-400 to-red-700 font-bold text-white' : this.tabClasses
                    },
                    changelog: {
                        label: "Changelog",
                        enabled: this.$gate.isAdmin() && ! this.fromReatlas,
                        showCount: true,
                        count: this.site.audits?.length || 0,
                        classes: this.tabClasses,
                        selectedClasses: this.selectedTabClasses
                    }
                }
            },

            // sites () {
            //     return this.$store.getters.sitesSortedBy(['building.state', 'building.city', 'building.address_line_1']);
            // }
        },

        methods: {
            getRoot(id) {
                return document.querySelector('#' + id)
            },

            showSites() {
                this.$modal.show('site-cards');
            },

            gotoEditBuilding() {
                location.href = `/buildings/${this.site.building.id}/edit`
            },

            openManageLeases(section) {
                this.leaseSection = section;

                this.$modal.show('manage-leases');
            },

            archiveSite() {
                this.site.archived_at = new Date().toISOString().slice(0, 19).replace('T', ' ');

                this.save();
            },

            unarchiveSite() {
                this.site.archived_at = null;

                this.save();
            },

            save() {
                axios.patch('/sites/' + this.site.id, this.site).then(response => {
                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: 'Site Saved'
                    });
                }).catch(error => {
                    console.error(error);
                })
            },

            confirmDeleteSite() {
                this.$swal({
                    title: 'Are you sure?',
                    text: "Are you sure you want to delete this site?",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.deleteSite();
                    }
                });
            },

            deleteSite() {
                axios.delete('/sites/' + this.site.id)
                    .then(response => {
                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: 'Site Deleted'
                        });

                        setTimeout(() => {
                            window.location = '/sites'
                        }, 1000);
                    })
                    .catch(error => {
                        this.$notify({
                            type: 'error',
                            title: 'Error',
                            text: error.response.data.message
                        });
                    }
                );
            },
            handleSiteImports(site_imports) {
                // Update the customers count in the tab
                this.tabs.site_imports.count = site_imports.length;
                
                // Update the site data with the new customers
                if (!this.site?.site_imports) {
                    this.$set(this.site, 'site_imports', []);
                }
                this.site.site_imports = site_imports;
                
            },

            canEdit() {
                console.log(this.$gate.can('edit', 'site', this.site), this.site, this.$gate);
                return this.$gate.can('edit', 'site', this.site);
            },
            canDelete() {
                return this.$gate.can('delete', 'site', this.site);
            }
        }
    }
</script>
